import {
  AmountCell,
  Breadcrumbs,
  Button,
  Inline,
  Stack,
  Typography,
  useModal,
} from '@omnipresentgroup/design-system';
import {
  useGetContractorInvoicesByIds,
  useGetContractorInvoicesReport,
} from '@omnipresentgroup/domains';
import {
  ContractorInvoiceStatus,
  ContractorInvoicesList,
} from '@omnipresentgroup/features';
import {
  type ContractorInvoice,
  ContractorInvoiceStateEnum,
} from '@omnipresentgroup/types';
import { createColumnHelper } from '@tanstack/react-table';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router';

import {
  CONTRACTOR_INVOICES_DETAILS_PATH,
  CONTRACTOR_INVOICES_PAYMENT_PATH,
} from '../../../constants';
import { useManagerUser } from '../../../hooks';
import { WrappedMixpanel } from '../../../mixpanelWrapper';
import { PaymentsSummaryModal } from './components';

export const CONTRACTOR_INVOICES_PAGE_TITLE = 'Contractor invoices';

const EMPTY_STATE_TITLE = 'No contractor invoice has been raised';
const EMPTY_STATE_DESCRIPTION =
  'When an invoice has been raised, it will show up on this page';

export const ContractorInvoices = () => {
  const navigate = useNavigate();

  const { userId } = useManagerUser();

  const [selectedInvoiceIds, setSelectedInvoiceIds] = useState<string[]>([]);

  const { modalProps } = useModal({});
  const [modalOpen, setModalOpen] = useState(false);

  /** Table Columns */
  const columnHelper = createColumnHelper<ContractorInvoice>();

  const columns = useMemo(
    () => [
      columnHelper.display({
        id: 'invoiced-amount',
        cell: (info) => (
          <AmountCell
            amount={info.row.original.amount}
            currency={info.row.original.currency}
          />
        ),
        header: () => 'Invoiced amount',
      }),
      columnHelper.display({
        id: 'billing-currency',
        cell: (info) => info.row.original.billingCurrency,
        header: () => 'Billing currency',
      }),
      columnHelper.display({
        id: 'manager-state',
        cell: (info) => (
          <ContractorInvoiceStatus
            invoice={info?.row.original}
            statusOverride={{
              [ContractorInvoiceStateEnum.OPEN]: {
                intent: 'warning',
                text: 'Open',
              },
              [ContractorInvoiceStateEnum.IN_PROGRESS]: {
                text: 'Awaiting transfer',
              },
            }}
          />
        ),
        header: () => 'Status',
      }),
      columnHelper.display({
        id: 'pay',
        cell: (info) => {
          const invoice = info.row.original;
          return ContractorInvoiceStateEnum.OPEN.includes(invoice.state) ? (
            <Button
              size="small"
              variant="secondary"
              type="button"
              onClick={(event: React.SyntheticEvent) => {
                WrappedMixpanel.track('Invoice Pay Clicked from List Page', {
                  userId,
                });
                event.stopPropagation();
                navigate(`${CONTRACTOR_INVOICES_PAYMENT_PATH}`, {
                  state: { invoicesIds: [invoice.id] },
                });
              }}
            >
              Pay
            </Button>
          ) : null;
        },
        size: 45,
      }),
    ],
    [columnHelper, navigate, userId],
  );

  const invoiceResults = useGetContractorInvoicesByIds({
    ids: selectedInvoiceIds,
  });

  const invoices = useMemo(
    () =>
      invoiceResults
        .map(({ data }: { data: ContractorInvoice | undefined }) => data)
        .filter(Boolean) as ContractorInvoice[],
    [invoiceResults],
  );

  const showModal = useMemo(() => {
    const allCurrencyEqual = invoices?.every(
      (invoice) => invoice?.currency === invoices[0]?.currency,
    );

    const allBillingCurrencyEqual = invoices?.every(
      (invoice) => invoice?.billingCurrency === invoices[0]?.billingCurrency,
    );

    const allOpenStates = invoices.every(
      (invoice) => invoice?.state === ContractorInvoiceStateEnum.OPEN,
    );

    return !allCurrencyEqual || !allBillingCurrencyEqual || !allOpenStates;
  }, [invoices]);

  const { downloadInvoices, downloadLink } = useGetContractorInvoicesReport({});

  return (
    <>
      <Stack gap="16">
        <Breadcrumbs
          crumbs={[
            { id: 'home', title: 'Home', onClick: () => navigate('/') },
            {
              id: 'contractor-invoices',
              title: CONTRACTOR_INVOICES_PAGE_TITLE,
              active: true,
            },
          ]}
        />
        <Inline minH={40} align="center" justify={'space-between'}>
          <Typography as="h3" hideParagraphSpacing size="24" weight="medium">
            {CONTRACTOR_INVOICES_PAGE_TITLE}
          </Typography>
          <Button id="export-invoices" onClick={() => downloadInvoices()}>
            Export invoices
          </Button>
        </Inline>
      </Stack>
      <ContractorInvoicesList
        columnVisibility={{
          invoice: true,
          contractor: true,
          dueDate: true,
          raisedAt: true,
          paidAt: true,
        }}
        columnsOverride={columns}
        emptyStateProps={{
          id: 'contractor-invoices-empty-list',
          title: EMPTY_STATE_TITLE,
          description: EMPTY_STATE_DESCRIPTION,
        }}
        onRowClick={(row: ContractorInvoice) => {
          if (selectedInvoiceIds.length === 0) {
            WrappedMixpanel.track('Invoice Clicked', {
              userId,
            });
            navigate(`${CONTRACTOR_INVOICES_DETAILS_PATH(row.id)}`);
          }
        }}
        useRowSelection
        showHeader
        tableActions={
          <Button
            variant="secondary"
            onClick={() =>
              showModal
                ? setModalOpen(true)
                : navigate(`${CONTRACTOR_INVOICES_PAYMENT_PATH}`, {
                    state: { invoicesIds: selectedInvoiceIds },
                  })
            } // if showModal is false, navigate to the payment page
            disabled={invoices.length === 0}
          >{`Pay invoices ${
            invoices.length > 0 ? `(${invoices.length})` : ''
          }`}</Button>
        }
        onSelectedRowsChange={(invoiceIds: string[]) => {
          setSelectedInvoiceIds(invoiceIds);
        }}
      />
      {invoices && (
        <PaymentsSummaryModal
          modalProps={{
            ...modalProps,
            open: modalOpen,
            onOpenChange: setModalOpen,
          }}
          invoices={invoices}
          handleClose={() => setModalOpen(false)}
        />
      )}
      <a href="#download" ref={downloadLink} hidden>
        Download Link
      </a>
    </>
  );
};
